/* global HTMLDivElement */

import * as React from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { useAtomValue, useSetAtom } from "jotai";
import {
  activeTabAtom,
  chatSelectionAtom,
  resetChatAtom,
  fontColorAtom,
  fontNameAtom,
  fontSizeAtom,
  insertPositionAtom,
  messageHistoryAtom,
  organizationIdAtom,
  productAtom,
  requestAtom,
} from "@/state";
import { chatSelectionForAnswer, getChatContext } from "@/utils/chat-selection";
import { CardData, AnswerImage } from "@/types";
import { copyToClipboard } from "@/utils/clipboard";
import { insertTextWithPosition } from "@/utils/editor-actions";
import {
  enhanceAnswerWithCitations,
  hasCitations as checkHasCitations,
  createChatMessageHistory,
  shouldShowCardHeader,
} from "@/utils/card-utils";
import { useCardMutations } from "@/hooks/useCardMutations";
import { CardHeader } from "./CardHeader";
import { CardActions } from "./CardActions";
import { CardCommentSection } from "./CardCommentSection";
import { CardContent } from "./CardContent";
import { CardCitations } from "./CardCitations";
const { useRef, useState, useCallback, useMemo } = React;

type CardProps = {
  data: CardData;
  variant?: "default" | "chat";
};

export const Card: React.FC<CardProps> = ({ data, variant = "default" }) => {
  const styles = useStyles();
  const contentRef = useRef<HTMLDivElement>(null);

  // Global state
  const product = useAtomValue(productAtom);
  const request = useAtomValue(requestAtom);
  const organizationId = useAtomValue(organizationIdAtom);
  const setActiveTab = useSetAtom(activeTabAtom);
  const setMessageHistory = useSetAtom(messageHistoryAtom);
  const chatSelection = useAtomValue(chatSelectionAtom);
  const setChatSelection = useSetAtom(chatSelectionAtom);
  const resetChat = useSetAtom(resetChatAtom);
  const insertPosition = useAtomValue(insertPositionAtom);
  const fontName = useAtomValue(fontNameAtom);
  const fontSize = useAtomValue(fontSizeAtom);
  const fontColor = useAtomValue(fontColorAtom);

  // Local state
  const [localData, setLocalData] = useState(data);
  const [isDisliked, setIsDisliked] = useState(data.metadata?.isDisliked || false);
  const [showCommentInput, setShowCommentInput] = useState(data.metadata?.isDisliked || false);
  const [commentText, setCommentText] = useState(data.metadata?.dislikeComment || "");
  const [isEditingComment, setIsEditingComment] = useState(false);

  // Sync with prop changes
  React.useEffect(() => {
    setLocalData(data);
    setIsDisliked(data.metadata?.isDisliked || false);
    setShowCommentInput(data.metadata?.isDisliked || false);
    setCommentText(data.metadata?.dislikeComment || "");
  }, [data]);

  // Mutations
  const mutations = useCardMutations({
    organizationId: organizationId!,
    productId: product?.id!,
    requestId: request?.id!,
  });

  // Computed values
  const enhancedAnswer = useMemo(
    () =>
      enhanceAnswerWithCitations(
        localData.answer,
        localData.citations,
        variant === "chat" ? chatSelection.productId : product?.id,
        variant === "chat" ? chatSelection.requestId : request?.id
      ),
    [
      localData.answer,
      localData.citations,
      product?.id,
      request?.id,
      variant,
      chatSelection.productId,
      chatSelection.requestId,
    ]
  );
  const hasCitations = useMemo(() => checkHasCitations(localData), [localData]);

  // Extract images from QA Bank citations
  const answerImages = useMemo((): AnswerImage[] => {
    const citationWithImages = localData.citations.qaBankCitations.find((c) => c.images && c.images.length > 0);
    return citationWithImages?.images || [];
  }, [localData.citations.qaBankCitations]);
  const shouldShowHeader = useMemo(() => shouldShowCardHeader(localData), [localData]);
  const showButtons = localData.answer !== "Information not found." && localData.answer !== "";
  const isChatVariant = variant === "chat";

  // Handlers
  const handleCopy = useCallback(async () => {
    await copyToClipboard(localData.answer);
  }, [localData.answer]);

  const handleInsert = useCallback(() => {
    insertTextWithPosition(localData.answer, insertPosition, { fontName, fontSize, fontColor });
  }, [localData.answer, insertPosition, fontName, fontSize, fontColor]);

  const handleNewChat = useCallback(() => {
    if (!organizationId || !product || !request) return;
    const selection = chatSelectionForAnswer(
      { ...chatSelection, organizationId, productId: product.id, requestId: request.id },
      localData
    );
    resetChat();
    setChatSelection(selection);
    if (getChatContext(selection)) setMessageHistory(createChatMessageHistory(localData));
    setActiveTab("chat");
  }, [
    localData,
    chatSelection,
    organizationId,
    product,
    request,
    resetChat,
    setChatSelection,
    setMessageHistory,
    setActiveTab,
  ]);

  const handleDislike = useCallback(() => {
    if (!organizationId || !product?.id || !request?.id) return;

    const newDisliked = !isDisliked;
    mutations.dislike.mutate({
      queryId: localData.id,
      isDisliked: newDisliked,
      dislikeComment: newDisliked ? undefined : "",
    });
    setIsDisliked(newDisliked);
    setShowCommentInput(newDisliked);
    if (!newDisliked) setCommentText("");
  }, [isDisliked, localData.id, mutations.dislike, organizationId, product, request]);

  const handleSendComment = useCallback(() => {
    if (!commentText.trim() || !organizationId || !product?.id || !request?.id) return;

    mutations.dislike.mutate({
      queryId: localData.id,
      isDisliked: true,
      dislikeComment: commentText,
    });
  }, [commentText, localData.id, mutations.dislike, organizationId, product, request]);

  const handleRegenerate = useCallback(() => {
    // Regenerate is Phase 2 — no-op for now
  }, []);

  const containerClass = isChatVariant ? styles.chatContainer : styles.container;

  return (
    <div className={containerClass}>
      {!isChatVariant && shouldShowHeader && (
        <CardHeader data={localData} productId={product?.id} requestId={request?.id} />
      )}

      <CardContent
        question={localData.question}
        enhancedAnswer={enhancedAnswer}
        images={answerImages}
        isLoading={false}
        showSpinner={false}
        contentRef={contentRef}
      />

      {showButtons && (
        <>
          <CardActions
            isChatVariant={isChatVariant}
            isDisliked={isDisliked}
            isRegenerating={false}
            isDislikeLoading={mutations.dislike.isPending}
            onInsert={handleInsert}
            onCopy={handleCopy}
            onNewChat={handleNewChat}
            onRegenerate={handleRegenerate}
            onDislike={handleDislike}
          />

          <CardCommentSection
            showCommentInput={showCommentInput}
            commentText={commentText}
            isEditingComment={isEditingComment}
            isPending={mutations.dislike.isPending}
            onCommentChange={setCommentText}
            onEditComment={() => setIsEditingComment(true)}
            onSendComment={() => {
              handleSendComment();
              setIsEditingComment(false);
            }}
            onFocus={() => setIsEditingComment(true)}
          />
        </>
      )}

      {hasCitations && <CardCitations citations={localData.citations} showDivider={showButtons} />}
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground2,
    padding: "24px",
    borderRadius: "16px",
    position: "relative",
  },
  chatContainer: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
    padding: "16px",
    borderRadius: "8px",
    minWidth: "60%",
    maxWidth: "100%",
  },
  modalOverlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.2)",
    zIndex: 999,
  },
  editAnswerModal: {
    position: "fixed" as const,
    top: "50px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    zIndex: 1001,
    padding: "24px",
    width: "90%",
    maxWidth: "600px",
    maxHeight: "80vh",
    overflow: "auto",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  modalTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "600" as const,
    color: "#333",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    color: "#999",
    padding: "0",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      color: "#333",
    },
  },
  editAnswerTextarea: {
    width: "100%",
    minHeight: "200px",
    padding: "12px",
    border: "1px solid #d0d0d0",
    borderRadius: "4px",
    fontFamily: "inherit",
    fontSize: "14px",
    resize: "vertical" as const,
    marginBottom: "16px",
  },
  modalActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
  },
  cancelButton: {
    padding: "8px 16px",
    border: "1px solid #d0d0d0",
    borderRadius: "4px",
    backgroundColor: "white",
    color: "#333",
    fontSize: "13px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  saveButton: {
    padding: "8px 16px",
    border: "none",
    borderRadius: "4px",
    backgroundColor: "#0078d4",
    color: "white",
    fontSize: "13px",
    cursor: "pointer",
    fontWeight: "600" as const,
    "&:hover": {
      backgroundColor: "#106ebe",
    },
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
});

export default React.memo(Card);
